import React, { useEffect, useState } from 'react';

import { matchPath, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import {
  useGetPermissionsQuery,
  useGetRolePermissionsQuery,
  useInsertRoleMutation,
  useUpdateRoleMutation,
} from '@school_ify/schoolify-repository/src/teacher/types';

import useForm from 'hooks/useForm';
import ModalDialog from 'components/ModalDialog/ModalDialog';
import TextField from 'clean/components/common/TextField/TextField';
import CheckBox from 'clean/components/common/CheckBox/CheckBox';

import RoleBadge from '../RoleBadge/RoleBadge';

import './RoleDetails.scss';

interface Props {
  onClose: () => void;
  onSave: () => void;
}

const RoleDetails: React.FC<Props> = ({ onClose, onSave }) => {
  const { t } = useTranslation();
  const [permissionsEnabled, setPermissionsEnabled] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [colour, setColour] = useState('#213960');

  const [formIsTouched, setFormIsTouched] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const location = useLocation();

  const match = matchPath<{ id: string }>(location.pathname, [
    '/permissions/roles/new',
    '/permissions/roles/:id',
  ]);

  const roleId = match.params.id;

  const isNew = roleId === undefined;

  const [insertRoleMutation] = useInsertRoleMutation();
  const [updateRoleMutation] = useUpdateRoleMutation();
  const { data: permissions, loading: loadingPermissions } = useGetPermissionsQuery();
  const { data: role, loading: loadingRole } = useGetRolePermissionsQuery({
    variables: {
      rolesByPkId: roleId,
    },
    skip: isNew,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (role) {
      setTitle(role.roles_by_pk.title);
      setColour(role.roles_by_pk.colour);
      setPermissionsEnabled(role.roles_by_pk.permissions.map(p => p.permission_id));
    }
  }, [role]);

  const onCheck = (permissionId: string) => {
    const isChecked = permissionsEnabled.includes(permissionId);
    if (isChecked) {
      setPermissionsEnabled(permissionsEnabled.filter(permission => permission !== permissionId));
    } else {
      setPermissionsEnabled([...permissionsEnabled, permissionId]);
    }
  };

  const form = useForm({
    errorHandler: set => {
      title.length === 0 && set('title', t('permissions:RoleDetailsNameRequired'));
      title.length > 50 && set('title', t('permissions:RoleDetailsNameTooLong', { max: 50 }));
    },
    isLoadingInitials: loadingPermissions || loadingRole,
    isLoadingActions: isSaving,
  });

  const onConfirm = async () => {
    setFormIsTouched(true);
    if (!form.isValid) {
      return;
    }
    try {
      setIsSaving(true);
      let selectedRoleId = null;
      if (isNew) {
        const data = await insertRoleMutation({
          variables: {
            object: { title, colour },
          },
        });
        selectedRoleId = data?.data?.insert_roles_one?.id;
      } else {
        await updateRoleMutation({
          variables: {
            pkColumns: {
              id: roleId,
            },
            set: {
              colour,
              title,
            },
          },
        });
        selectedRoleId = roleId;
      }

      await axios.post(
        `${process.env.REACT_APP_SERVICES_ENDPOINT}/api/permissions/index`,
        {
          permission_ids: permissionsEnabled,
          role_id: selectedRoleId,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Access-Control-Allow-Origin': '*',
            'Content-type': 'application/json',
          },
        }
      );
      onSave();
      onClose();
    } catch (err) {
      console.warn(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalDialog
      open={true}
      isLoadingContent={form.isLoadingInitials}
      isLoadingActions={form.isLoadingActions}
      onClose={onClose}
      title={isNew ? t('permissions:RoleDetailsNewRole') : t('permissions:RoleDetailsEditRole')}
      error={!form.isValid && formIsTouched ? t('common:all_fileds') : ''}
      actions={[
        {
          type: 'filled',
          colour: 'primary',
          fullWidth: true,
          label: isNew ? t('permissions:RoleDetailsCreate') : t('permissions:RoleDetailsUpdate'),
          handler: onConfirm,
        },
      ]}
    >
      <div className="roleDetails">
        <div className="defaultHorizontalPadding">
          <TextField
            label={t('permissions:RoleDetailsName')}
            autoFocus={isNew}
            errors={form.errors}
            formIsTouched={formIsTouched}
            property="title"
            value={title}
            onChange={e => {
              setTitle(e.target.value);
            }}
          />
        </div>

        <div className="defaultHorizontalPadding">
          <div className="title">{t('permissions:RoleDetailsRoleColor')}</div>
          <div className="colorContainer">
            <input
              type="color"
              value={colour}
              onChange={e => {
                setColour(e.target.value);
              }}
            />
            <div className="backdrop"></div>
            <RoleBadge color={colour} />
          </div>
        </div>
        <div className="line"></div>
        <div className="defaultHorizontalPadding">
          <div className="title">{t('permissions:RoleDetailsPrivileges')}</div>
        </div>
        <div>
          {permissions?.permissions.map(permission => {
            return (
              <div key={permission.id} className="item" onClick={() => onCheck(permission.id)}>
                <CheckBox
                  color="orange"
                  onChange={() => null}
                  checked={permissionsEnabled.includes(permission.id)}
                  style={{ pointerEvents: 'none' }}
                />
                <div className="label">{permission.title}</div>
              </div>
            );
          })}
        </div>
      </div>
    </ModalDialog>
  );
};

export default RoleDetails;
